import styled, { css } from 'styled-components';
import { theme } from 'modules/ui/theme';
import { Subtitle, Label } from 'modules/ui/typography';
import { mediaQuery } from 'modules/utils';
import { textClamp } from 'modules/utils/cssUtils';
import {
  NavigationAndActionProblem,
  NavigationAndActionSingleTick,
} from '../icons';
import { StepState } from './StepState';

const stepColor = (state?: StepState, isFailedStep?: boolean) => {
  if (isFailedStep) {
    return theme.colors.primaryR1;
  }
  switch (state) {
    case StepState.Previous:
      return theme.colors.primaryK10;
    case StepState.Current:
      return theme.colors.primaryT2;
    default:
      return theme.colors.primaryK7;
  }
};

const smallIconStyle = css`
  height: 0.896rem;
  width: 1.231rem;
  g {
    fill: ${theme.colors.primaryW};
  }

  ${mediaQuery('md')} {
    height: 0.695rem;
    width: 0.93rem;
  }
`;

export const StyledStepContainer = styled.div<{
  state?: StepState;
  isFailedStep?: boolean;
}>`
  height: 1.875rem;
  width: 1.875rem;
  background: ${(props) => stepColor(props.state, props.isFailedStep)};
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${mediaQuery('md')} {
    height: 1.313rem;
    width: 1.313rem;
  }
`;

export const StyledTickSmallIcon = styled(NavigationAndActionSingleTick)`
  ${smallIconStyle};
`;

export const StyledProblemSmallIcon = styled(NavigationAndActionProblem)`
  ${smallIconStyle};
`;

export const StyledStepNumber = styled(Subtitle)`
  color: ${theme.colors.primaryW};
  font-size: 1.125rem;
  margin-bottom: 0;

  ${mediaQuery('md')} {
    font-size: 0.875rem;
  }
`;

export const StyledStepText = styled(Label)<{
  state?: StepState;
  isFailedStep?: boolean;
  lastStep?: boolean;
}>`
  user-select: none;
  height: 1.5rem;
  color: ${(props) => stepColor(props.state, props.isFailedStep)};

  margin-bottom: 0;
  padding-right: 30px;

  ${(props) => {
    if (props.state === StepState.Current)
      return css`
        width: auto;
        max-width: 100%;

        margin-top: -1.7rem;
        margin-left: 2.5rem;
        display: flex;
        align-items: center;
        justify-content: left;
      `;
    if (!props.lastStep)
      return css`
        width: 6.8rem;
        max-width: unset;

        text-indent: 100%;
        white-space: nowrap;
        overflow: hidden;
      `;
    if (props.lastStep)
      return css`
        width: 6.8rem;
        max-width: unset;

        display: none;
      `;
    return null;
  }}

  ${mediaQuery('md')} {
    width: auto;
    max-width: 15rem;
    margin-top: 0.625rem;
    margin-left: unset;

    font-size: var(--h4-font-size);
    line-height: var(--h4-line-height);
    text-transform: unset;

    align-items: unset;
    justify-content: unset;
    text-indent: unset;
    white-space: unset;
    overflow: unset;

    display: inline-block;
  }
`;

export const StyledClamp = styled.div`
  ${textClamp({
    maxLines: 2,
    lineHeight: '0.963rem',
  })}

  ${mediaQuery('md')} {
    ${textClamp({
      maxLines: 2,
      lineHeight: '0.5625rem',
    })}
  }
`;

export const HorizontalBar = styled.div<{
  state?: StepState;
}>`
  height: 1.875rem;
  width: ${(props) => (props.state === StepState.Current ? '100%' : '3.75rem')};
  min-width: auto;
  margin-left: -0.9375rem;
  background: ${theme.colors.primaryK1};
  z-index: -1;

  ${mediaQuery('md')} {
    height: 1.313rem;
    width: 100%;
    min-width: 12rem;
    margin-left: -0.6565rem;
  }
`;

export const Horizontal = styled.div`
  display: flex;
`;

export const FlexWrapper = styled.div`
  display: flex;
  overflow-x: scroll;

  ${mediaQuery('md')} {
    overflow-x: unset;
  }
`;

export const StepWrapper = styled.div<{
  firstStep?: boolean;
  afterCurrent?: boolean;
}>`
  margin-left: ${(props) => {
    if (props.firstStep) return '0';
    if (props.afterCurrent) return '-0.9rem';
    return '-3.16rem';
  }};

  ${mediaQuery('md')} {
    margin-left: ${(props) => (props.firstStep ? '0' : '-0.6565rem')};
  }
`;
